import React, { useState, useEffect } from 'react';
import Image from '../AppImage';
import Button from './Button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* SVG definition for the rounded trapezoid clip path */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <clipPath id="roundedTrapezoid" clipPathUnits="objectBoundingBox">
            <path d="M 0.2,1 
                     C 0.1,1, 0,0.9, 0,0.8
                     L 0,0.2 
                     C 0,0.1, 0.1,0, 0.2,0 
                     L 0.8,0 
                     C 0.9,0, 1,0.1, 1,0.2 
                     L 1,0.8 
                     C 1,0.9, 0.9,1, 0.8,1 
                     L 0.2,1 Z" />
          </clipPath>
        </defs>
      </svg>

      {/* Main Header */}
      <header
        className={`relative z-[1000] transition-all duration-300 ${isScrolled
            ? 'bg-black/95 backdrop-blur-nav shadow-nav'
            : 'bg-black/90 backdrop-blur-nav'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-center h-16 md:h-20 w-full pt-2 relative">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-50 text-white hover:text-primary transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              <svg
                className="w-5 h-5 md:w-6 md:h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            {/* Left side navigation */}
            <div className="absolute left-1/2 top-1/2 transform -translate-x-full -translate-y-1/2 -ml-40 flex space-x-8 hidden lg:flex">
              <button
                onClick={() => scrollToSection('services')}
                className="group relative inline-flex items-center justify-center text-base rounded-full bg-transparent border-2 border-white px-6 py-2 font-semibold text-white transition-all duration-200 hover:bg-white hover:text-primary hover:shadow-lg hover:-translate-y-0.5 hover:shadow-white/30"

                onMouseEnter={(e) => e.target.style.color = '#ebcc89'}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('locations')}
                className="group relative inline-flex items-center justify-center text-base rounded-full bg-transparent border-2 border-white px-6 py-2 font-semibold text-white transition-all duration-200 hover:bg-white hover:text-primary hover:shadow-lg hover:-translate-y-0.5 hover:shadow-white/30"

                onMouseEnter={(e) => e.target.style.color = '#ebcc89'}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                Standorte
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="group relative inline-flex items-center justify-center text-base rounded-full bg-transparent border-2 border-white px-6 py-2 font-semibold text-white transition-all duration-200 hover:bg-white hover:text-primary hover:shadow-lg hover:-translate-y-0.5 hover:shadow-white/30"

                onMouseEnter={(e) => e.target.style.color = '#ebcc89'}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                Kontakt
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="group relative inline-flex items-center justify-center text-base rounded-full bg-transparent border-2 border-white px-6 py-2 font-semibold text-white transition-all duration-200 hover:bg-white hover:text-primary hover:shadow-lg hover:-translate-y-0.5 hover:shadow-white/30"

                onMouseEnter={(e) => e.target.style.color = '#ebcc89'}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                Beschwerden
              </button>
            </div>

            {/* Logo - Centered */}
            <div className="w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 bg-white shadow-lg flex-shrink-0 mt-8 md:mt-12 lg:mt-18" style={{
              clipPath: 'url(#roundedTrapezoid)',
              transform: 'perspective(1000px) rotateX(10deg)',
              transformOrigin: 'center bottom'
            }}>
              <div className="w-full h-full p-4 md:p-6 lg:p-8" style={{
                transform: 'perspective(1000px) rotateX(-10deg)',
                transformOrigin: 'center bottom'
              }}>
                <Image
                  src="/assets/images/Ragnalogo.jpg"
                  alt="Ragna Detailing Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Right side contact info and booking button */}
            <div className="absolute left-1/2 top-1/2 transform translate-x-full -translate-y-1/2 -ml-[15rem] flex items-center space-x-3 hidden lg:flex">
              <a
                href="tel:0 2064 777722"
                className="text-white text-sm md:text-base font-bold hover:text-primary transition-colors duration-200 cursor-pointer"
                title="Dinslaken anrufen"
              >
                Dinslaken: 0 2064 777722
              </a>
              <button
                onClick={() => scrollToSection('contact')}
                className="group relative inline-flex items-center justify-center text-sm md:text-base rounded-full bg-transparent border-2 border-white px-4 md:px-6 py-2 font-semibold text-white transition-all duration-200 hover:bg-white hover:text-primary hover:shadow-lg hover:-translate-y-0.5 hover:shadow-white/30"
              >
                Termin vereinbaren
                <svg
                  aria-hidden="true"
                  viewBox="0 0 10 10"
                  height="8"
                  width="8"
                  fill="none"
                  className="mt-0.5 ml-1 md:ml-2 -mr-1 stroke-white stroke-1.5 md:stroke-2"
                >
                  <path
                    d="M0 5h7"
                    className="transition opacity-0 group-hover:opacity-100"
                  ></path>
                  <path
                    d="M1 1l4 4-4 4"
                    className="transition group-hover:translate-x-[3px]"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed top-16 md:top-20 left-0 right-0 bg-black/95 backdrop-blur-nav shadow-nav z-[999] border-t border-white/20">
          <div className="px-4 md:px-6 py-4 space-y-4">
            {/* Mobile Navigation Links */}
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => scrollToSection('services')}
                className="text-white text-base md:text-lg font-semibold hover:text-primary transition-colors duration-200 text-left"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('locations')}
                className="text-white text-base md:text-lg font-semibold hover:text-primary transition-colors duration-200 text-left"
              >
                Standorte
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-white text-base md:text-lg font-semibold hover:text-primary transition-colors duration-200 text-left"
              >
                Kontakt
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-white text-base md:text-lg font-semibold hover:text-primary transition-colors duration-200 text-left"
              >
                Beschwerden
              </button>
            </div>

            {/* Mobile Contact Info */}
            <div className="pt-4 border-t border-white/20">
              <a
                href="tel:0 2064 777722"
                className="text-white text-base md:text-lg font-bold hover:text-primary transition-colors duration-200 block mb-3"
                title="Dinslaken anrufen"
              >
                Dinslaken: 0 2064 777722
              </a>
              <button
                onClick={() => scrollToSection('contact')}
                className="w-full bg-primary text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold hover:bg-primary/90 transition-colors duration-200 text-sm md:text-base"
              >
                Termin vereinbaren
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;